<template>
  <section class="lymphomap_main">
    <div class="vista_video_wrp">
      <!-- Header -->
      <div class="vista_video_header_wrp">
        <div class="row align-items-center">
          <div class="col">
            <h2 class="main_heading">
              <button
                type="button"
                class="btn back_btn"
                @click="$router.go(-1)"
                aria-label="Back"
              >
                <i class="fa fa-chevron-left" aria-hidden="true"></i>
              </button>
              Vista
            </h2>
            <p class="vista_main_subheading">Welcome to Lymphomap's Media Page</p>
          </div>
        </div>
      </div>

      <!-- Small Overlay Loader -->
      <div v-if="isLoading" class="httploader">
        <i class="fa fa-spinner fa-spin fa-3x"></i>
      </div>

      <!-- Video Sections Body -->
      <div
        class="vista_video_body_wrp"
        v-else-if="popularVideosResults && popularVideosResults.length"
      >
        <!-- Public Video Playlists -->
        <div
          class="media_video_section"
          v-for="(videoList, index) in popularVideosResults"
          :key="videoList.etag || index"
        >
          <!-- Dynamic Category Header -->
          <h4
            v-if="categoryMeta[index]"
            :class="['media_heading', categoryMeta[index].customClass]"
          >
            {{ categoryMeta[index].title }}

            <div class="desc_describe_txt">
              <i class="fa fa-info-circle" aria-hidden="true"></i>
              <div class="desc_tooltip_text">
                <p
                  v-for="(para, pIdx) in categoryMeta[index].descriptions"
                  :key="pIdx"
                  :class="pIdx === categoryMeta[index].descriptions.length - 1 ? 'mb-0' : 'mb-1'"
                >
                  {{ para }}
                </p>
              </div>
            </div>

            <div v-if="categoryMeta[index].pdf" class="pdf_for_conference">
              <i
                class="fa fa-file-pdf-o"
                aria-hidden="true"
                @click="openPdfViewer(categoryMeta[index].pdf.url, categoryMeta[index].pdf.name)"
              ></i>
            </div>
          </h4>

          <!-- Category Slider -->
          <div
            class="media_video_slider"
            v-if="videoList.items && filterValidThumbnails(videoList.items).length"
          >
            <carousel
              :mouseDrag="true"
              :touchDrag="false"
              :autoplay="false"
              :loop="false"
              :nav="false"
              :margin="15"
              :dots="false"
              :items="4"
              :responsive="carouselBreakpoints"
            >
              <template slot="prev">
                <span class="media_slider_arrow media_slider_prev">
                  <i class="fa fa-angle-left" aria-hidden="true"></i>
                </span>
              </template>

              <div
                v-for="popularVideo in filterValidThumbnails(videoList.items)"
                :key="popularVideo.id"
              >
                <div class="video_details">
                  <router-link
                    :to="{
                      name: 'VistaMediaInfo',
                      params: { videoID: getVideoId(popularVideo) }
                    }"
                  >
                    <div class="video_thumb">
                      <img
                        :src="getThumbnailUrl(popularVideo)"
                        :alt="popularVideo.snippet.title"
                        class="img-fluid"
                      />
                    </div>
                    <div class="playerinfo">
                      <h3 class="player_name" :title="popularVideo.snippet.title">
                        {{ popularVideo.snippet.title }}
                      </h3>
                      <div class="player_designation">{{ popularVideo.snippet.description }}</div>
                      <div class="video_uploaded_date">
                        <i class="fa fa-upload mr-1"></i>
                        {{ popularVideo.snippet.publishedAt | LocalDateFormat }}
                      </div>
                    </div>
                  </router-link>
                </div>
              </div>

              <template slot="next">
                <span
                  :class="[
                    'media_slider_arrow media_slider_next',
                    { mobile_view_display: index !== 0 }
                  ]"
                >
                  <i class="fa fa-angle-right" aria-hidden="true"></i>
                </span>
              </template>
            </carousel>
          </div>
        </div>

        <!-- Exclusive Videos Section -->
        <div
          class="media_video_section"
          v-if="exclusiveVideoDataResults && exclusiveVideoDataResults.length"
        >
          <h4 class="media_heading">
            Exclusive Videos
            <div class="desc_describe_txt">
              <i class="fa fa-info-circle" aria-hidden="true"></i>
              <div class="desc_tooltip_text exclusive_video_desc_wrp">
                <p class="mb-0">
                  Witness our "exclusive videos" playlist specifically created for our Lymphomap
                  members. As a valued member, you will have access to all our exclusive content and
                  more.
                </p>
              </div>
            </div>
          </h4>

          <div class="media_video_slider">
            <carousel
              :mouseDrag="true"
              :touchDrag="false"
              :autoplay="false"
              :loop="false"
              :nav="false"
              :margin="15"
              :dots="false"
              :items="4"
              :responsive="carouselBreakpoints"
            >
              <template slot="prev">
                <span class="media_slider_arrow media_slider_prev">
                  <i class="fa fa-angle-left" aria-hidden="true"></i>
                </span>
              </template>

              <div v-for="video in exclusiveVideoDataResults" :key="video.id">
                <div class="video_details">
                  <router-link
                    :to="{ name: 'VistaPrivateMediaInfo', params: { videoID: video.id } }"
                  >
                    <div class="video_thumb">
                      <img :src="video.thumbnail" :alt="video.title" class="img-fluid" />
                    </div>
                    <div class="playerinfo">
                      <h3 class="player_name" :title="video.title">{{ video.title }}</h3>
                      <div class="player_designation" v-html="video.description"></div>
                      <div class="video_uploaded_date">
                        <i class="fa fa-upload mr-1"></i>
                        {{ video.created_at | LocalDateFormat }}
                      </div>
                    </div>
                  </router-link>
                </div>
              </div>

              <template slot="next" v-if="exclusiveVideoDataResults.length > 4">
                <span class="media_slider_arrow media_slider_next">
                  <i class="fa fa-angle-right" aria-hidden="true"></i>
                </span>
              </template>
            </carousel>
          </div>
        </div>
      </div>

      <!-- Fallback message (only shown when loading is finished AND no data exists) -->
      <div v-else-if="!isLoading" class="httploader">
        <i class="fa fa-spinner fa-spin fa-3x"></i>
      </div>
    </div>

    <!-- PDF Viewer Modal -->
    <pdf-viewer
      v-if="pdfViewerPopup"
      :pdfViewerURL="pdfViewerUrl"
      :pdfViewerFileNAME="pdfViewerFileName"
    ></pdf-viewer>
  </section>
</template>

<script>
import carousel from 'vue-owl-carousel';
import { bus } from '@/main';
import pdfViewer from './pdf-viewer';

export default {
  name: 'VistaConference',
  components: {
    carousel,
    'pdf-viewer': pdfViewer
  },
  data() {
    return {
      isLoading: true,
      popularVideosData: null,
      popularVideosResults: [],
      exclusiveVideoData: null,
      exclusiveVideoDataResults: [],
      pdfViewerPopup: false,
      pdfViewerUrl: '',
      pdfViewerFileName: '',
      carouselBreakpoints: {
        0: { items: 1 },
        1024: { items: 4 },
        1900: { items: 6 }
      },
      categoryMeta: [
        {
          title: 'Global Conference Videos',
          customClass: '',
          descriptions: [
            'On October 31st, 2021, Lymphomap hosted its global launch conference virtually! We had senior oncologists, experts, and professionals in the medical industry attend and witness how Lymphomap bridges the worlds of Precision Medicine, Oncology, Artificial Intelligence and Machine Learning.',
            'From having esteemed guest speakers to hosting an interactive style series of presentations and discussions - our global launch depicted how Lymphomap paves the way for the future of oncology. We not only discussed Lymphomap’s multitude of features all inbuilt on our platform but we also showcased our Core AI Engine.'
          ],
          pdf: null
        },
        {
          title: 'Ashray Public Webinar',
          customClass: '',
          descriptions: [
            'On November 26th, 2021, Ashray hosted their public webinar on Lymphomap’s video conferencing platform. Lymphomap would like to thank both Ashray and the Tata Medical Center’s Hematology Department for choosing Lymphomap to host their webinar.',
            'From having experts to brave survivors, the webinar showcased various journeys and the ultimate game plan in defeating Lymphoma and assisting others affected by cancer. Discover Ashray’s noble cause and how they are making a difference in the world, one survivor and patient at a time.'
          ],
          pdf: {
            url: 'https://lymphomap-chatbot-data.s3.ap-south-1.amazonaws.com/Welcoming_Ashray_-_The_Warriors_of_Lymphoma.pdf',
            name: 'Welcoming Ashray The Warriors of Lymphoma'
          }
        },
        {
          title: "LBIL's Launch Videos",
          customClass: 'lbil_launch_info',
          descriptions: [
            'On July 17th, 2022, Lymphomap hosted its latest product launch, LBIL, virtually! We had NGO founders, experts, healthcare professionals, and the general public attend. We portrayed how LBIL is a globally networked virtual hospital on the cloud that transcends digital healthcare using Artificial Intelligence and Machine Learning. From having interactive style series of presentations to practical tours and live demonstrations, our global launch depicted LBIL as the bridge to the future for personalized and state-of-the-art healthcare and support systems. We not only showcased LBIL’s multitude of features all inbuilt on our platform, but we also explored the many possibilities of global connectivity in the healthcare field. Furthermore, our core NGO and Support Groups’ workspace invites global members to engage in a safe and secure space for the right kind of attention and care.'
          ],
          pdf: null
        },
        {
          title: 'Demystifying Lymphoma: Global Lymphoma Awareness Week',
          customClass: 'jascap_info',
          descriptions: [
            'This year, Lymphomap proudly hosted an international webinar to kickstart Global Lymphoma Awareness Week (September 11th - 16th, 2023). We were honored to collaborate with the nationally renowned JASCAP Organization from Mumbai. On September 14th at 11:30 am IST/11:00 pm PST, distinguished oncology, psychology and public healthcare experts came together to spread awareness and discuss the journey of fighting and winning the battle against Lymphoma. After the talks by our panelists, we had an interactive Q&A Discussion further deconstructing the methods to diagnose, treat and care for our bodies and minds effectively.'
          ],
          pdf: {
            url: 'https://lymphomap-chatbot-data.s3.ap-south-1.amazonaws.com/Lymphomap+x+JASCAP+Webinar+Flyer+-+OFFICIAL.pdf',
            name: 'Global Lymphoma Awareness Week: Demystifying Lymphoma'
          }
        }
      ]
    };
  },
  computed: {
    publicMediaState() {
      return this.$store.getters.getVistaPublicMediaListState;
    }
  },
  watch: {
    publicMediaState: {
      immediate: true,
      handler(val) {
        if (val && val.data && val.data.length) {
          this.popularVideosData = val;
          this.popularVideosResults = val.data;
          this.isLoading = false;
        }
      }
    }
  },
  mounted() {
    bus.$on('pdfViewerPopupBus', this.handlePdfPopupToggle);
    this.fetchData();
  },
  beforeDestroy() {
    bus.$off('pdfViewerPopupBus', this.handlePdfPopupToggle);
    document.body.classList.remove('pdf_view_actv');
  },
  methods: {
    async fetchData() {
      // 1. If public media is already populated in Vuex, sync immediately
      if (this.publicMediaState && this.publicMediaState.data && this.publicMediaState.data.length) {
        this.popularVideosData = this.publicMediaState;
        this.popularVideosResults = this.publicMediaState.data;
      }

      // 2. Fetch private/exclusive media list
      try {
        const res = await this.$store.dispatch('getPrivateMediaListFun');
        if (res && res.data) {
          this.exclusiveVideoData = res.data;
          this.exclusiveVideoDataResults = res.data.data || [];
        }
      } catch (err) {
        console.error('Failed to load private media list:', err);
      } finally {
        this.isLoading = false;
      }
    },
    filterValidThumbnails(items) {
      if (!Array.isArray(items)) return [];
      return items.filter((item) => {
        var thumbs = item && item.snippet && item.snippet.thumbnails;
        return thumbs && thumbs.default != null;
      });
    },
    getVideoId(item) {
      return (
        (item &&
          item.snippet &&
          item.snippet.resourceId &&
          item.snippet.resourceId.videoId) ||
        ''
      );
    },
    getThumbnailUrl(item) {
      var thumbs = item && item.snippet && item.snippet.thumbnails;
      if (!thumbs) return '';
      return (thumbs.medium && thumbs.medium.url) || (thumbs.default && thumbs.default.url) || '';
    },
    openPdfViewer(url, fileName) {
      this.pdfViewerFileName = fileName;
      this.pdfViewerUrl = url;
      this.pdfViewerPopup = true;
      document.body.classList.add('pdf_view_actv');
    },
    handlePdfPopupToggle(isOpen) {
      this.pdfViewerPopup = isOpen;
      if (!isOpen) {
        document.body.classList.remove('pdf_view_actv');
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.httploader {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.72);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
}

.vista_video_wrp {
  color: #ffffff;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica Neue, Arial, sans-serif;
  letter-spacing: 1px;

  .main_heading {
    color: #d29e00;
    font-size: 40px;
    margin: 0;
    font-family: 'origin_tech_demoregular';
  }

  .back_btn {
    vertical-align: middle;
  }

  .vista_main_subheading {
    font-size: 14px;
    text-transform: capitalize;
    font-family: arial;
    color: #bbb;
    margin-bottom: 30px;
    line-height: 1;
    padding-left: 42px;

    @media only screen and (max-width: 767px) {
      font-size: 12px;
    }
  }

  .btn-primary {
    cursor: pointer;
    position: relative;
    min-width: 95px;
    box-shadow: none !important;
    outline: none !important;
    background: rgba(255, 255, 255, 0.12) !important;
    color: #ffffff !important;
    border: none;
    border-radius: 3px;
    font-family: 'MyriadProRegular', 'Lato', sans-serif;
    font-size: 18px;

    &:hover {
      color: #000 !important;
      background: #87cefa !important;
    }
  }

  .vista_video_body_wrp {
    padding: 0 30px 20px;

    @media only screen and (max-width: 1023px) {
      padding: 0 15px 20px;
    }

    a {
      color: inherit;
      text-decoration: none;
    }

    .video_details {
      background: linear-gradient(
        -30deg,
        rgba(37, 44, 84, 0.6) 0%,
        rgba(0, 0, 0, 0.6) 50%,
        rgba(38, 38, 38, 0.6) 50%,
        rgba(44, 44, 84, 0.6) 100%
      );
      border: 1px solid rgba(93, 93, 93, 0.4);
      border-radius: 8px;
      text-align: left;
      transform: scale(1);
      transition: transform 0.5s ease;
      transform-origin: center center;

      &:hover {
        transform: scale(1.01);

        .player_name {
          color: #ffd400;
        }
      }

      a {
        display: block;
        padding: 10px;
      }
    }

    .playerinfo {
      margin-top: 8px;
    }

    .media_heading {
      font-size: 24px;
      font-family: 'the_bold_fontbold';
      margin-bottom: 10px;
      line-height: 1;
      color: #fffcc4;

      @media only screen and (max-width: 767px) {
        font-size: 16px;
        line-height: 1.3;
      }
    }

    .video_thumb {
      height: 200px;
      display: block;
      max-width: 100%;

      .img-fluid {
        height: 100%;
        width: 100%;
        object-fit: cover;
        object-position: center center;
      }
    }

    .player_name {
      font-size: 18px;
      font-weight: 600;
      line-height: 1.3;
      margin: 0 0 5px;
      font-family: "BeonMedium", sans-serif;
      height: 45px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: normal;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;
      display: -webkit-box;
      color: #87cefa;
    }

    .player_designation,
    .player_place {
      font-size: 16px;
      color: #d6d6d6;
      font-family: 'georgiaregular';
      line-height: 1.3;
      margin-bottom: 5px;
      height: 40px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: normal;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;
      display: -webkit-box;
    }

    .video_uploaded_date,
    .video_views {
      color: #747474;
      font-size: 13px;
      font-family: 'MyriadProRegular', 'Lato', sans-serif;
    }

    .desc_describe_txt {
      display: inline-block;
      margin-left: 10px;
      position: relative;

      .fa {
        cursor: pointer;

        &:hover {
          color: #87cefa;
        }
      }

      .desc_tooltip_text {
        left: -325px;
        position: absolute;
        background: #fff176;
        padding: 8px 10px;
        cursor: default;
        z-index: 999;
        font-weight: 600;
        line-height: 1.3;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica Neue, Arial, sans-serif;
        font-size: 16px;
        color: #000;
        top: 31px;
        border-radius: 2px;
        width: 660px;
        display: none;
        text-align: justify;
        letter-spacing: 0;

        @media only screen and (max-width: 767px) {
          width: 280px;
          left: auto;
          font-size: 14px;
          top: 24px;
          right: -25px;
        }

        &::before {
          border: solid;
          border-color: #fff176 transparent;
          border-width: 12px 12px 0 0;
          top: -6px;
          content: "";
          display: block;
          left: 50%;
          position: absolute;
          z-index: 1000;
          transform: rotate(45deg);

          @media only screen and (max-width: 767px) {
            left: auto;
            right: 25px;
          }
        }

        &.exclusive_video_desc_wrp {
          left: -205px;
          width: 420px;
        }
      }

      .fa:hover + .desc_tooltip_text {
        display: block;
      }
    }

    .lbil_launch_info {
      .desc_tooltip_text {
        left: -275px !important;

        @media only screen and (max-width: 767px) {
          left: auto !important;
          right: -75px;
          width: 300px;
          font-size: 14px;
          z-index: 999;
        }

        &:before {
          left: 42.5% !important;

          @media only screen and (max-width: 767px) {
            left: auto !important;
            right: 76px;
          }
        }
      }
    }

    .jascap_info {
      .desc_tooltip_text {
        left: -325px !important;

        @media only screen and (max-width: 767px) {
          left: auto !important;
          right: -125px;
          width: 300px;
          font-size: 14px;
          z-index: 999;
        }

        @media only screen and (max-width: 440px) {
          right: -30px;
        }

        &:before {
          left: 50% !important;

          @media only screen and (max-width: 767px) {
            left: auto !important;
            right: 126px;
          }

          @media only screen and (max-width: 440px) {
            right: 30px;
          }
        }
      }
    }

    .media_video_slider {
      position: relative;

      .media_slider_arrow {
        position: absolute;
        z-index: 100;
        top: 50%;
        left: -21px;
        transform: translateY(-50%);

        .fa {
          cursor: pointer;
          font-size: 42px;
          line-height: 28px;
          color: #ffc107;

          &:hover {
            color: #87cefa;
          }
        }

        &.media_slider_next {
          right: -21px;
          left: auto;
        }
      }
    }
  }

  .media_video_section {
    margin-bottom: 30px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .pdf_for_conference {
    display: inline-block;
    margin-left: 15px;

    .fa {
      color: #fffcc4;
      cursor: pointer;

      &:hover {
        color: #87cefa;
      }
    }
  }
}

.mobile_view_display {
  display: none !important;

  @media only screen and (max-width: 767px) {
    display: block !important;
  }
}

@media only screen and (max-width: 1360px) and (min-width: 768px) {
  .vista_video_wrp {
    .vista_main_heading {
      font-size: 32px;
    }
    .media_heading {
      font-size: 20px !important;
    }
    .vista_main_subheading {
      margin-bottom: 25px !important;
    }
    .video_thumb {
      height: 160px !important;
    }
    .player_name {
      font-size: 15px !important;
      height: 40px !important;
    }
    .player_designation {
      height: 35px !important;
      font-size: 14px !important;
    }
    .more_video_vista.pt-5 {
      padding-top: 20px !important;

      .btn-primary {
        font-size: 22px !important;
        line-height: 40px !important;
      }
    }
  }
}
</style>